// A question object for meterID interactive:

import type { TimeSignature } from '../notation';
import type { AnswerAndData } from './interactiveQuestionPrototype';
import type MeterID from './meterID';
import type { RhythmQuestionObject } from './rhythmQuestionProcessor';
import RhythmQuestionProcessor from './rhythmQuestionProcessor';

export default class Question extends RhythmQuestionProcessor {
  skills: string[];

  declare timeSignature: TimeSignature & Required<Pick<TimeSignature, 'displayAs'>>;

  /** String representation of correct time signature answer */
  get answer(): string {
    const aliases = [
      ['2/4', '4/8'],
      ['2/2', '4/4'],
    ];
    const alias = aliases.find((aliasArray) => aliasArray.includes(this.timeSignature.toString()));
    if (alias) return alias.join(' or ');
    return this.timeSignature.toString();
  }

  /**
   * Create a Question from a rhythmOrMusicString, where
   * rhythmOrMusicString in format such as:
   *   RhythmString: "12/8: tempo=62 q. q. h. | h. e e e e e e | h. h.r",
   *     or "6/8: e e e q. | e e e q. | q. q. | e e e q."
   *   MusicString: See 'Potential music storage format.md'
   */
  constructor(rhythmOrMusicString: string, questionNumber: number);
  /**
   * Create a Question from an object -- typically this is one pulled from database of questions.
   * @param questionObject - a question object pulled from database
   */
  constructor(questionObject: RhythmQuestionObject, questionNumber: number);
  /**
   * Create a question from a music string
   * @param musicString - the music string
   * @param questionNumber - the question number
   */
  constructor(musicString: string, questionNumber: number);
  constructor(rhythmOrMusicStringOrObject: string | RhythmQuestionObject, questionNumber: number);
  constructor(rhythmOrMusicStringOrObject: string | RhythmQuestionObject, questionNumber: number) {
    super(rhythmOrMusicStringOrObject, questionNumber);
    this.timeSignature.displayAs = '1/1';
    this.skills = this.timeSignature.isCompound ? ['meter_id_compound'] : ['meter_id_simple'];
  }

  check(): boolean {
    const aliases = [
      ['2/4', '4/8'],
      ['2/2', '4/4'],
    ];
    const alias = aliases.find((aliasArray) => aliasArray.includes(this.timeSignature.toString()));
    if (alias) return alias.includes(this.timeSignature.displayAs);
    return this.timeSignature.toString() === this.timeSignature.displayAs;
  }

  getAnswerAndData(interactive?: MeterID): AnswerAndData {
    const correct = this.check();
    const skills = this.skills;
    return {
      skills,
      question: this.rhythmOrMusicString,
      questionType: 'MeterID',
      questionText: 'What meter is this music in?',
      questionSVG: undefined,
      studentAnswerText: this.timeSignature.displayAs,
      studentAnswerSVG: interactive ? interactive.notation!.exportSVG() : '',
      correctAnswerText: this.answer,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
    };
  }
}
